<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getFlatNodeCatalog } from '../composables/nodeCatalog';

const props = defineProps({
  open: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'select']);

const { t } = useI18n();

const query = ref('');
const inputRef = ref(null);
const listRef = ref(null);
const selectedIndex = ref(0);

// Catalog is small enough (~50 items) that a simple substring match is fine —
// no need for a fuzzy lib. Score-ordered: name matches rank above category
// matches; prefix matches above middle matches.
const items = computed(() => getFlatNodeCatalog(t));

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return items.value.slice(0, 50);
  return items.value
    .map(item => {
      const label = (item.label || '').toLowerCase();
      const category = (item.categoryLabel || '').toLowerCase();
      const sub = (item.data?.type || '').toLowerCase();
      let score = -1;
      if (label.startsWith(q)) score = 100;
      else if (label.includes(q)) score = 60;
      else if (sub.startsWith(q)) score = 40;
      else if (sub.includes(q)) score = 30;
      else if (category.includes(q)) score = 10;
      return { item, score };
    })
    .filter(x => x.score >= 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 50)
    .map(x => x.item);
});

watch(
  () => props.open,
  open => {
    if (open) {
      query.value = '';
      selectedIndex.value = 0;
      nextTick(() => inputRef.value?.focus());
    }
  }
);

watch(query, () => {
  selectedIndex.value = 0;
});

const scrollSelectedIntoView = () => {
  nextTick(() => {
    const el = listRef.value?.querySelector('[data-cmd-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  });
};

const handleKey = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    emit('close');
    return;
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    if (filtered.value.length === 0) return;
    selectedIndex.value = (selectedIndex.value + 1) % filtered.value.length;
    scrollSelectedIntoView();
    return;
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault();
    if (filtered.value.length === 0) return;
    selectedIndex.value =
      (selectedIndex.value - 1 + filtered.value.length) % filtered.value.length;
    scrollSelectedIntoView();
    return;
  }
  if (event.key === 'Enter') {
    event.preventDefault();
    const item = filtered.value[selectedIndex.value];
    if (item) emit('select', item);
  }
};

const pickByClick = (item, index) => {
  selectedIndex.value = index;
  emit('select', item);
};

const handleBackdropClick = event => {
  if (event.target === event.currentTarget) emit('close');
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      :aria-label="t('PIPELINE.FLOW_BUILDER.COMMAND_PALETTE.TITLE')"
      @click="handleBackdropClick"
    >
      <div
        class="w-full max-w-xl mx-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak shadow-2xl overflow-hidden"
      >
        <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
          <span
            class="i-lucide-search size-4 text-n-slate-10 flex-shrink-0"
            aria-hidden="true"
          />
          <input
            ref="inputRef"
            v-model="query"
            type="text"
            class="flex-1 bg-transparent text-sm text-n-slate-12 placeholder-n-slate-9 outline-none"
            :placeholder="
              t('PIPELINE.FLOW_BUILDER.COMMAND_PALETTE.PLACEHOLDER')
            "
            :aria-label="t('PIPELINE.FLOW_BUILDER.COMMAND_PALETTE.PLACEHOLDER')"
            @keydown="handleKey"
          />
          <kbd
            class="px-1.5 py-0.5 text-[10px] font-mono font-medium text-n-slate-10 bg-n-alpha-1 rounded border border-n-weak hidden sm:inline-block"
          >
            {{ t('PIPELINE.FLOW_BUILDER.COMMAND_PALETTE.ESC') }}
          </kbd>
        </div>

        <div
          ref="listRef"
          class="max-h-[50vh] overflow-y-auto py-1"
          role="listbox"
        >
          <p
            v-if="filtered.length === 0"
            class="px-4 py-6 text-sm text-n-slate-10 text-center"
          >
            {{ t('PIPELINE.FLOW_BUILDER.COMMAND_PALETTE.NO_RESULTS') }}
          </p>
          <button
            v-for="(item, idx) in filtered"
            :key="`${item.type}-${item.data?.type || idx}-${idx}`"
            type="button"
            role="option"
            :aria-selected="idx === selectedIndex"
            :data-cmd-active="idx === selectedIndex ? 'true' : 'false'"
            class="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors"
            :class="
              idx === selectedIndex
                ? 'bg-n-alpha-2 text-n-slate-12'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @mouseenter="selectedIndex = idx"
            @click="pickByClick(item, idx)"
          >
            <span
              class="flex items-center justify-center size-6 rounded-md flex-shrink-0 text-[10px] font-semibold uppercase"
              :class="{
                'bg-blue-3 text-blue-11': item.type === 'trigger',
                'bg-amber-3 text-amber-11': item.type === 'condition',
                'bg-teal-3 text-teal-11': item.type === 'action',
                'bg-n-alpha-1 text-n-slate-11': item.type === 'annotation',
              }"
            >
              {{ item.type.charAt(0) }}
            </span>
            <span class="flex-1 min-w-0">
              <span class="block text-sm font-medium truncate">
                {{ item.label }}
              </span>
              <span class="block text-xs text-n-slate-10 truncate">
                {{ item.categoryLabel }}
              </span>
            </span>
            <kbd
              v-if="idx === selectedIndex"
              class="px-1.5 py-0.5 text-[10px] font-mono font-medium text-n-slate-10 bg-n-alpha-1 rounded border border-n-weak"
            >
              {{ t('PIPELINE.FLOW_BUILDER.COMMAND_PALETTE.ENTER') }}
            </kbd>
          </button>
        </div>

        <div
          class="px-4 py-2 border-t border-n-weak flex items-center justify-between text-[11px] text-n-slate-10"
        >
          <span class="flex items-center gap-4">
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-chevron-up size-3" aria-hidden="true" />
              <span class="i-lucide-chevron-down size-3" aria-hidden="true" />
              {{ t('PIPELINE.FLOW_BUILDER.COMMAND_PALETTE.NAVIGATE') }}
            </span>
            <span class="flex items-center gap-1.5">
              <span
                class="i-lucide-corner-down-left size-3"
                aria-hidden="true"
              />
              {{ t('PIPELINE.FLOW_BUILDER.COMMAND_PALETTE.INSERT') }}
            </span>
          </span>
          <span>{{ filtered.length }}</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>
